import { useState } from 'react';
import { AttentionTriangleIcon, CheckIcon, CopyIcon } from '@rsos/assets/icons';
import { Button, Loader, Modal } from '@rsos/base-components';
import {
  ModalBody,
  ModalFooter,
} from '@rsos/components/capstone/base/Modal/ModalComponents';
import { formatInternationalPhoneNumberHuman } from '@rsos/utils/internationalPhoneNumberUtils';
import styles from './IntlCallbackModal.module.css';

interface Props {
  isOpened: boolean;
  onClose: () => void;
  onRetry: () => void;
  pin: string | null;
  bridgeNumber: string | null;
  callerPhoneNumber?: string | null;
  isCreatingSession?: boolean;
  error?: string | null;
}

const IntlCallbackModal = ({
  isOpened,
  onClose,
  onRetry,
  pin,
  bridgeNumber,
  callerPhoneNumber = null,
  isCreatingSession = false,
  error = null,
}: Props) => {
  const digits = (pin || '').split('');
  const formattedNumber = bridgeNumber
    ? formatInternationalPhoneNumberHuman(bridgeNumber)
    : null;
  const formattedCallerNumber = callerPhoneNumber
    ? formatInternationalPhoneNumberHuman(callerPhoneNumber)
    : null;

  const [isPinCopied, setIsPinCopied] = useState(false);
  const [isNumberCopied, setIsNumberCopied] = useState(false);

  const copyToClipboard = (text: string, onCopied: (v: boolean) => void) => {
    if (!navigator.clipboard) {
      // eslint-disable-next-line no-console -- browser capability not available
      console.error(
        'Copy feature unavailable. Only available on a secure (HTTPS) connection.',
      );
      return;
    }
    navigator.clipboard.writeText(text);
    onCopied(true);
    setTimeout(() => onCopied(false), 3000);
  };

  const handleCopyPin = () => copyToClipboard(pin || '', setIsPinCopied);

  const handleCopyNumber = () =>
    copyToClipboard((bridgeNumber || '').replace(/\D/g, ''), setIsNumberCopied);

  const handleNumberKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCopyNumber();
    }
  };

  const hasSessionData = pin && bridgeNumber;

  return (
    <Modal
      isOpened={isOpened}
      onClose={onClose}
      width={350}
      id="intl-callback-modal"
      data-name="intl-callback-modal"
    >
      <ModalBody onClose={onClose}>
        {isCreatingSession ? (
          <div className={styles.loadingState} role="status">
            <Loader fluid text="Creating callback session..." />
          </div>
        ) : error ? (
          <div className={styles.stateContainer} role="alert">
            <div className={styles.errorHeader}>
              <AttentionTriangleIcon
                aria-hidden
                width={20}
                height={20}
                color="heavyDanger"
              />
              <span className={styles.errorTitle}>Callback Unavailable</span>
            </div>
            <p className={styles.errorMessage}>{error}</p>
          </div>
        ) : hasSessionData ? (
          <div className={styles.stateContainer}>
            <p className={styles.callInstruction}>
              Call{' '}
              <span className={styles.phoneNumberWrapper}>
                <span
                  className={styles.phoneNumber}
                  data-id="bridge-number"
                  role="button"
                  tabIndex={0}
                  aria-label={`Copy phone number ${formattedNumber}`}
                  onClick={handleCopyNumber}
                  onKeyDown={handleNumberKeyDown}
                >
                  {formattedNumber}
                </span>
                <span className={styles.phoneTooltip} aria-hidden>
                  {isNumberCopied ? 'Copied' : 'Copy'}
                </span>
              </span>{' '}
              to start the international call
            </p>
            <p className={styles.callDetails}>
              Enter the 4-digit PIN when prompted. Harmony will then dial{' '}
              {formattedCallerNumber} and bridge the call.
            </p>
            <div
              className={styles.pinRow}
              role="group"
              aria-label="4-digit PIN"
            >
              {digits.map((digit, i) => (
                <div key={i} className={styles.pinBox}>
                  {digit}
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </ModalBody>

      <ModalFooter flow={12}>
        {isCreatingSession ? null : error ? (
          <>
            <Button
              btnType="tertiary"
              onClick={onClose}
              grow
              noMargin
              dataName="intl-callback-close-button"
            >
              Close
            </Button>
            <Button
              btnType="primary"
              onClick={onRetry}
              grow
              noMargin
              dataName="intl-callback-retry-button"
            >
              Try Again
            </Button>
          </>
        ) : hasSessionData ? (
          <>
            <Button
              btnType="tertiary"
              onClick={onClose}
              grow
              noMargin
              dataName="intl-callback-close-button"
            >
              Close
            </Button>
            <Button
              btnType="primary"
              onClick={handleCopyPin}
              grow
              noMargin
              dataName="intl-callback-copy-pin-button"
            >
              <div className={styles.buttonContent}>
                {isPinCopied ? (
                  <CheckIcon aria-hidden height={12} width={12} />
                ) : (
                  <CopyIcon aria-hidden height={12} width={12} />
                )}
                {isPinCopied ? 'Copied' : 'Copy PIN'}
              </div>
            </Button>
          </>
        ) : (
          <Button
            btnType="tertiary"
            onClick={onClose}
            grow
            noMargin
            dataName="intl-callback-close-button"
          >
            Close
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default IntlCallbackModal;
